/**
 * Utility functions for detecting and handling strategic alignment errors
 *
 * Strategic alignment errors occur when trying to update L1/L2 tier moments
 * without having Audience Strategy Mappings (Must Win selections) filled.
 */

/** Tiers that require strategic alignment (Must Wins) - used for dynamic messaging */
const STRATEGIC_ALIGNMENT_TIERS = ['L1', 'L2'] as const;

/**
 * Builds a dynamic error message for strategic alignment based on tier context.
 * Avoids hardcoding "L1 and L2" when the error may apply to a specific tier.
 *
 * @param tierContext - Optional: the tier(s) that triggered the error (e.g. from update, or parsed from error)
 * @param error - Optional: the API error to parse for tier mentions
 * @returns User-friendly message with correct tier(s) mentioned
 */
export const getStrategicAlignmentErrorMessage = (
  tierContext?: string | string[],
  error?: any
): string => {
  let tiers: string[] = [];

  // 1. Use explicit tier context when available (e.g. from tier dropdown update)
  if (tierContext) {
    const arr = Array.isArray(tierContext) ? tierContext : [tierContext];
    tiers = arr.filter(t =>
      STRATEGIC_ALIGNMENT_TIERS.includes(t as any)
    ) as string[];
  }

  // 2. Try to parse tier from error message if no context
  if (tiers.length === 0 && error?.message) {
    const msg = String(error.message);
    if (msg.includes('L1') && msg.includes('L2')) tiers = ['L1', 'L2'];
    else if (msg.includes('L2')) tiers = ['L2'];
    else if (msg.includes('L1')) tiers = ['L1'];
  }

  // 3. Default to L1 and L2 when unknown (covers most cases)
  if (tiers.length === 0) tiers = [...STRATEGIC_ALIGNMENT_TIERS];

  const tierPhrase =
    tiers.length === 2
      ? `${tiers[0]} and ${tiers[1]}`
      : tiers.length === 1
      ? tiers[0]
      : tiers.join(', ');

  return `Strategic Must Wins are required for all ${tierPhrase} RSDs.\n\nPlease add the appropriate Must Win(s) for your Customer Audiences(s), before proceeding.`;
};

/**
 * Detects if an error is related to strategic alignment requirements
 * @param error - The error object from the API call
 * @returns true if the error is about strategic alignment being mandatory
 */
export const detectStrategicAlignmentError = (error: any): boolean => {
  const errorMessage = error?.message || '';

  // Check for multiple possible message variations
  const strategicAlignmentKeywords = [
    'Audience Strategy Mappings is mandatory',
    'audienceStrategyMappings',
    'Audience Strategy Mappings',
    'customer audience'
  ];

  // Check if error message contains any of the keywords
  const hasStrategicAlignmentKeyword = strategicAlignmentKeywords.some(
    keyword => errorMessage.toLowerCase().includes(keyword.toLowerCase())
  );

  // Check if it mentions L1 or L2 specifically in the context of the error
  const mentionsL1OrL2 =
    (errorMessage.includes('L1') || errorMessage.includes('L2')) &&
    (errorMessage.includes('tier') || errorMessage.includes('mandatory'));

  return hasStrategicAlignmentKeyword && mentionsL1OrL2;
};
